import { ROLES, api } from '@agency/shared';
import { Button, Chip, DataTable, PageHeader, type DataTableColumn } from '@agency/ui';
import { useNavigate } from '@tanstack/react-router';
import { UserPlus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ErrorCallout, FilterInput, FilterSelect, LoadMore, Page, RoleChips, Stamp, TableCard } from '../../app/widgets.js';
import { ROLE_SHORT, USER_STATUS_TONE, pluralize } from '../../lib/labels.js';
import { useSession } from '../../lib/session.js';
import { useApi, usePaged } from '../../lib/useApi.js';
import { InviteDialog } from './InviteDialog.js';

export function PeopleListPage() {
  const session = useSession();
  const navigate = useNavigate();
  const [status, setStatus] = useState<api.UserStatus | undefined>(undefined);
  const [role, setRole] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const input = useMemo(() => ({ limit: 100, ...(status ? { status } : {}), ...(role ? { role: role as api.User['roles'][number] } : {}), ...(search ? { search } : {}) }), [status, role, search]);
  const people = usePaged('admin.people.list', { input });
  const accounts = useApi('admin.accounts.list', { input: { limit: 200 } }, { enabled: session.has(['account.assign', 'account.register', 'audit.read']) });
  const canManage = session.has('people.manage');

  const columns = useMemo<DataTableColumn<api.User>[]>(
    () => [
      { id: 'name', header: 'Name', accessorKey: 'displayName', cell: ({ row }) => <span className="text-ink">{row.original.displayName}</span>, size: 200 },
      { id: 'email', header: 'Email', accessorKey: 'email', cell: ({ row }) => <span className="font-mono text-xs-plus text-mute">{row.original.email}</span>, size: 230 },
      { id: 'roles', header: 'Roles', cell: ({ row }) => <RoleChips roles={row.original.roles} />, size: 220 },
      { id: 'status', header: 'Status', accessorKey: 'status', cell: ({ row }) => <Chip tone={USER_STATUS_TONE[row.original.status]}>{row.original.status}</Chip>, size: 100 },
      { id: 'accounts', header: 'Expected accounts', cell: ({ row }) => <span className="block text-right font-mono tabular-nums" title="Informational; nothing enforces expected accounts">{row.original.accountIds.length}</span>, size: 130 },
      // real-providers change: the Direct send column is gone; Agency creates Gmail drafts only.
      { id: 'lastSeen', header: 'Last sign-in', accessorKey: 'lastSeenAt', cell: ({ row }) => <Stamp value={row.original.lastSeenAt} relative />, size: 110 },
    ],
    [],
  );

  const total = people.total ?? people.items.length;
  const active = people.items.filter((p) => p.status === 'active').length;

  return (
    <Page>
      <PageHeader
        eyebrow="People and access"
        eyebrowPath="People"
        title="People"
        subtitle={people.loading ? 'Loading staff.' : `${pluralize(total, 'person', 'people')} · ${active} active · ${people.items.filter((p) => p.status === 'invited').length} invited · ${people.items.filter((p) => p.status === 'suspended').length} suspended`}
        actions={
          <Button variant="primary" icon={UserPlus} disabled={!canManage} onClick={() => setInviteOpen(true)}>
            Invite person
          </Button>
        }
      />
      <ErrorCallout error={people.error} onRetry={() => void people.reload()} className="mt-6" />
      <div className="mt-6">
        <TableCard title="Staff" subtitle={`${total} shown`} footer={<LoadMore hasMore={people.hasMore} loading={people.loadingMore} onLoadMore={() => void people.loadMore()} />}>
          <div className="border-b border-hair px-5 py-3">
            <div className="flex flex-wrap items-end gap-3">
              <FilterInput label="Search" value={search} onChange={setSearch} placeholder="Name or email" width={240} />
              <FilterSelect label="Status" value={status} onChange={(v) => setStatus(v as api.UserStatus | undefined)} options={api.USER_STATUSES.map((s) => ({ value: s, label: s }))} width={140} />
              <FilterSelect label="Role" value={role} onChange={setRole} options={ROLES.map((r) => ({ value: r, label: ROLE_SHORT[r] }))} width={160} />
            </div>
          </div>
          <DataTable<api.User>
            aria-label="People"
            columns={columns}
            rows={people.items}
            rowKey={(r) => r.id}
            height={Math.min(720, Math.max(240, people.items.length * 34 + 40))}
            state={people.loading ? 'loading' : people.items.length === 0 ? 'empty' : 'complete'}
            emptyMessage="No people match these filters."
            noun="people"
            onActivate={(id) => void navigate({ to: '/people/$userId', params: { userId: id } })}
            onSelect={(id) => void navigate({ to: '/people/$userId', params: { userId: id } })}
          />
        </TableCard>
      </div>
      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} accounts={accounts.data?.items ?? []} />
    </Page>
  );
}
